import { useEffect, useState } from "react";
import { Star, Tag } from "lucide-react";
import { fetchAdminSalesSurveys } from "@/lib/api";

const PRIX = { oui: "Oui", partiellement: "Partiellement", non: "Non" };
const DUREE = { moins_1_semaine: "< 1 semaine", "1_4_semaines": "1-4 semaines", plus_1_mois: "> 1 mois" };
const Bar = ({ label, n, total, testid }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={testid}>
    <span className="w-28 text-neutral-600">{label}</span>
    <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: total ? `${(n / total) * 100}%` : 0 }} /></div>
    <span className="w-8 text-right font-bold">{n}</span>
  </div>
);

export const SalesSurveysSection = () => {
  const [data, setData] = useState(null);
  useEffect(() => { fetchAdminSalesSurveys().then(setData).catch(() => setData({ stats: { total: 0 }, items: [] })); }, []);
  if (!data) return null;
  const { stats, items } = data;
  return (
    <section className="glass-card p-6 mt-8 lg:col-span-2 xl:col-span-3 w-full" data-testid="sales-surveys-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-extrabold inline-flex items-center gap-2"><Tag size={18} className="text-emerald-600" /> Ventes & satisfaction post-vente</h2>
        <div className="flex gap-4 text-sm">
          <span data-testid="sales-sold-total"><b>{stats.sold_total}</b> annonces vendues</span>
          <span data-testid="sales-survey-total"><b>{stats.total}</b> questionnaires</span>
          <span className="inline-flex items-center gap-1" data-testid="sales-note-moyenne"><Star size={14} className="fill-amber-400 text-amber-400" /> <b>{stats.note_moyenne ?? "—"}</b>/5</span>
        </div>
      </div>
      {stats.total > 0 && (
        <div className="mt-4 grid md:grid-cols-3 gap-5">
          <div><div className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">Prix conforme aux attentes</div>{Object.entries(PRIX).map(([k, l]) => <Bar key={k} label={l} n={stats.prix[k]} total={stats.total} testid={`sales-prix-${k}`} />)}</div>
          <div><div className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">Duree de vente</div>{Object.entries(DUREE).map(([k, l]) => <Bar key={k} label={l} n={stats.duree[k]} total={stats.total} testid={`sales-duree-${k}`} />)}</div>
          <div><div className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">Recommandation</div>{[5, 4, 3, 2, 1].map((k) => <Bar key={k} label={`${k} etoile${k > 1 ? "s" : ""}`} n={stats.notes[String(k)]} total={stats.total} testid={`sales-note-${k}`} />)}</div>
        </div>
      )}
      {items.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-neutral-500"><tr><th className="py-1">Date</th><th>Annonce</th><th>Categorie</th><th>Prix</th><th>Duree</th><th>Note</th></tr></thead>
            <tbody>
              {items.slice(0, 30).map((i) => (
                <tr key={i.id} className="border-t border-neutral-100" data-testid={`sales-row-${i.annonce_id}`}>
                  <td className="py-1.5 whitespace-nowrap">{new Date(i.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="max-w-[320px] truncate pr-3">{i.annonce_title}</td><td>{i.category}</td>
                  <td>{PRIX[i.prix_attentes]}</td><td>{DUREE[i.duree_vente]}</td><td className="font-bold">{i.recommandation}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
